import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Tooltip from './Tooltip';

interface Persona {
  title: string;
  description: string;
  keyPoints: (string | React.ReactNode)[];
}

const personas: Persona[] = [
  {
    title: "Conservation Practitioners, Governments & Marine Protected Areas",
    description: "Evidence-based conservation strategies and ecosystem monitoring for effective marine protection",
    keyPoints: [
      "Robust biodiversity data for MPA design and effectiveness evaluation",
      "Food web modeling and ecosystem connectivity mapping",
      "Real-time monitoring of invasive, endangered, and keystone species",
      "Adaptive fisheries management through ecosystem-level analysis",
      "Community-centered partnerships for equitable conservation"
    ]
  },
  {
    title: "Low & Middle Income Countries",
    description: "Sustainable blue economic development through biotechnology and nature-based solutions",
    keyPoints: [
      (<>
        Turnkey <Tooltip definition="Marine Genetic Resources (MGRs) are the genetic material contained in marine organisms, including microbes, plants, and animals, that have actual or potential value. MGRs may include genes coding for enzymes, peptides, or other biomolecules with applications in biotechnology, medicine, and industry." term="Marine Genetic Resources">Marine Genetic Resources</Tooltip> discovery with sovereign <Tooltip definition="Benefit-sharing refers to adhering to international and national frameworks, foremost the Nagoya Protocol, that regulate access to genetic resources and ensure that benefits arising from their use (scientific, financial, or technological) are shared fairly and equitably with the country or community of origin." term="Benefit-sharing">benefit-sharing</Tooltip>
      </>),
      "Access to multilateral funding through enhanced biotechnological capacities",
      "Sustainable blue economy development, including eco-tourism and marine stewardship",
      "Transparent, equitable approaches building long-term trust",
      "Scientific expertise and technological innovation transfer"
    ]
  },
  {
    title: "Nature-Based Finance & Investment Community",
    description: "Data-driven ecological risk assessment and investment opportunity identification",
    keyPoints: [
      "Ecological risk evaluation and investment opportunity insights",
      "High-impact intervention identification through vulnerability analysis",
      "Due diligence support with holistic ecosystem network context",
      "Robust metrics for measuring conservation outcomes",
      "De-risking nature-based finance instruments"
    ]
  },
  {
    title: "Corporate Sector",
    description: "Science-based biodiversity intelligence for nature-positive corporate strategies",
    keyPoints: [
      "Credible, science-based biodiversity intelligence for nature-positive strategies",
      "Regulatory compliance with auditable data for Corporate Disclosure Frameworks",
      "Nature-dependent asset valuation and environmental forecasting",
      "Catastrophic event modeling and ecosystem-linked risk assessment",
      "Measurable conservation impact demonstration"
    ]
  },
  {
    title: "Foundations & Philanthropic Institutions",
    description: "Evidence-driven conservation strategies and financial mechanisms powered by comprehensive marine data",
    keyPoints: [
      "Science-based conservation strategy development with biodiversity intelligence informing MPA design and ecosystem management",
      "Innovative conservation finance mechanisms to accelerate effective marine protection",
      "Local capacity building and technology transfer through biotechnology training and infrastructure development",
      "Transparent impact measurement and reporting providing auditable conservation outcomes data",
      "Strategic funding optimization using ecosystem analysis to identify high-impact intervention opportunities"
    ]
  }
];

const UserPersonasSection: React.FC = () => {
  const [expandedItems, setExpandedItems] = useState<Set<number>>(new Set());

  const toggleExpanded = (index: number) => {
    setExpandedItems(prev => {
      const newSet = new Set(prev);
      if (newSet.has(index)) {
        newSet.delete(index);
      } else {
        newSet.add(index);
      }
      return newSet;
    });
  };

  return (
    <section id="who-we-serve" className="py-5" style={{
      background: `
        linear-gradient(135deg, rgba(10, 14, 20, 0.9) 0%, rgba(20, 25, 31, 0.9) 30%, rgba(30, 37, 45, 0.9) 70%, rgba(42, 53, 65, 0.9) 100%),
        url('/images/background-images/whale.jpg')
      `,
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      backgroundRepeat: 'no-repeat',
      backgroundAttachment: 'fixed',
      padding: '5rem 0',
      position: 'relative'
    }}>
      <div className="container">
        {/* Header Section */}
        <div className="row justify-content-center mb-5">
          <div className="col-lg-10 text-center">
            <h2 style={{
              fontSize: 'clamp(1.75rem, 4vw, 2.5rem)',
              fontWeight: 700,
              marginBottom: '2rem',
              letterSpacing: '-0.02em',
              lineHeight: 1.3,
              color: '#EFEFE3',
              fontFamily: "'Montserrat', sans-serif"
            }}>
              Who We Serve
            </h2>
            <p style={{
              fontSize: '1.25rem',
              color: '#D5D6CC',
              lineHeight: '1.8',
              maxWidth: '900px',
              margin: '0 auto',
              marginBottom: '3rem'
            }}>
              Investing in ocean health? Supporting marine conservation? Seeking innovative financing mechanisms to fund MPAs? Ocean BioMetrics delivers the actionable intelligence you need.
            </p>
          </div>
        </div>

        {/* Expandable List Section */}
        <div className="row justify-content-center">
          <div className="col-lg-10">
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '1rem',
              maxWidth: '1000px',
              margin: '0 auto'
            }}>
              {personas.map((persona, index) => {
                const isExpanded = expandedItems.has(index);

                return (
                  <div
                    key={index}
                    style={{
                      background: 'rgba(239, 239, 227, 0.04)',
                      backdropFilter: 'blur(10px)',
                      border: '1px solid rgba(239, 239, 227, 0.08)',
                      borderRadius: '15px',
                      overflow: 'hidden',
                      transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                      boxShadow: isExpanded
                        ? '0 15px 50px rgba(24, 195, 186, 0.15)'
                        : '0 5px 20px rgba(0,0,0,0.15)',
                      borderColor: isExpanded
                        ? 'rgba(24, 195, 186, 0.3)'
                        : 'rgba(239, 239, 227, 0.08)',
                      position: 'relative'
                    }}
                  >
                    {/* Clickable Header */}
                    <div
                      onClick={() => toggleExpanded(index)}
                      style={{
                        padding: window.innerWidth < 768 ? '1rem 1rem' : '1.75rem 2rem',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        userSelect: 'none',
                        position: 'relative',
                        transition: 'background 0.3s ease',
                        background: isExpanded
                          ? 'rgba(239, 239, 227, 0.02)'
                          : 'transparent'
                      }}
                      onMouseEnter={(e) => {
                        if (!isExpanded) {
                          e.currentTarget.style.background = 'rgba(239, 239, 227, 0.02)';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!isExpanded) {
                          e.currentTarget.style.background = 'transparent';
                        }
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: window.innerWidth < 768 ? '0.75rem' : '1.5rem', flex: 1 }}>
                        {/* Icon */}
                        <div style={{
                          width: window.innerWidth < 768 ? '45px' : '60px',
                          height: window.innerWidth < 768 ? '45px' : '60px',
                          flexShrink: 0,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}>
                          <img
                            src={`/images/icons/persona${index + 1}.png`}
                            alt={`${persona.title} icon`}
                            style={{
                              width: '50px',
                              height: '50px',
                              objectFit: 'contain',
                              filter: 'brightness(0) invert(1)',
                              opacity: 0.85,
                              transition: 'all 0.3s ease',
                              transform: isExpanded ? 'scale(1.1)' : 'scale(1)'
                            }}
                            onError={(e) => {
                              e.currentTarget.style.display = 'none';
                              const fallback = e.currentTarget.parentElement;
                              if (fallback) {
                                fallback.innerHTML = `
                                  <div style="
                                    width: 50px;
                                    height: 50px;
                                    background: rgba(24, 195, 186, 0.2);
                                    border-radius: 10px;
                                    display: flex;
                                    align-items: center;
                                    justify-content: center;
                                    font-size: 1.5rem;
                                    font-weight: 700;
                                    color: #18C3BA;
                                    font-family: 'Montserrat', sans-serif;
                                  ">
                                    0${index + 1}
                                  </div>
                                `;
                              }
                            }}
                          />
                        </div>

                        {/* Title */}
                        <h3 style={{
                          fontSize: window.innerWidth < 768 ? 'clamp(0.9rem, 3vw, 1.1rem)' : 'clamp(1rem, 2vw, 1.3rem)',
                          fontWeight: 600,
                          color: '#EFEFE3',
                          margin: 0,
                          lineHeight: 1.4,
                          fontFamily: "'Montserrat', sans-serif",
                          paddingRight: window.innerWidth < 768 ? '2rem' : '3rem'
                        }}>
                          {persona.title}
                        </h3>
                      </div>

                      {/* Plus/Minus Icon */}
                      <div style={{
                        width: '32px',
                        height: '32px',
                        borderRadius: '50%',
                        background: isExpanded
                          ? 'rgba(24, 195, 186, 0.15)'
                          : 'rgba(239, 239, 227, 0.05)',
                        border: isExpanded
                          ? '1px solid rgba(24, 195, 186, 0.3)'
                          : '1px solid rgba(239, 239, 227, 0.1)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        transition: 'all 0.3s ease',
                        flexShrink: 0
                      }}>
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 16 16"
                          fill="none"
                          style={{
                            transition: 'transform 0.3s ease',
                            transform: isExpanded ? 'rotate(45deg)' : 'rotate(0deg)'
                          }}
                        >
                          <path
                            d="M8 3V13M3 8H13"
                            stroke={isExpanded ? '#18C3BA' : '#D5D6CC'}
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </div>
                    </div>

                    {/* Expandable Content */}
                    <div style={{
                      maxHeight: isExpanded ? (window.innerWidth < 768 ? '800px' : '600px') : '0',
                      opacity: isExpanded ? 1 : 0,
                      transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                      overflow: 'hidden'
                    }}>
                      <div style={{
                        padding: window.innerWidth < 768 ? '0 1rem 1.5rem 1rem' : '0 2rem 2rem 2rem',
                        transform: isExpanded ? 'translateY(0)' : 'translateY(-10px)',
                        transition: 'transform 0.4s cubic-bezier(0.4, 0, 0.2, 1)'
                      }}>
                        {/* Description */}
                        <p style={{
                          color: '#18C3BA',
                          fontSize: '1.1rem',
                          fontWeight: 500,
                          lineHeight: '1.6',
                          marginBottom: '1.5rem',
                          paddingLeft: window.innerWidth < 768 ? '0' : '4.5rem'
                        }}>
                          {persona.description}
                        </p>

                        {/* Key Points */}
                        <div style={{
                          paddingLeft: window.innerWidth < 768 ? '0' : '4.5rem',
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '0.75rem'
                        }}>
                          {persona.keyPoints.map((point, pointIndex) => (
                            <div key={pointIndex} style={{
                              display: 'flex',
                              alignItems: 'flex-start',
                              gap: '0.75rem',
                              padding: '0.75rem',
                              background: 'rgba(239, 239, 227, 0.02)',
                              borderRadius: '10px',
                              border: '1px solid rgba(24, 195, 186, 0.1)',
                              transition: 'all 0.3s ease'
                            }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.background = 'rgba(239, 239, 227, 0.04)';
                                e.currentTarget.style.borderColor = 'rgba(24, 195, 186, 0.2)';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.background = 'rgba(239, 239, 227, 0.02)';
                                e.currentTarget.style.borderColor = 'rgba(24, 195, 186, 0.1)';
                              }}
                            >
                              <div style={{
                                width: '6px',
                                height: '6px',
                                backgroundColor: '#18C3BA',
                                borderRadius: '50%',
                                marginTop: '0.5rem',
                                flexShrink: 0,
                                boxShadow: '0 0 6px rgba(24, 195, 186, 0.4)'
                              }} />
                              <span style={{
                                color: '#D5D6CC',
                                fontSize: '0.95rem',
                                lineHeight: '1.6',
                                fontWeight: 400
                              }}>
                                {point}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>

                    {/* Bottom accent line */}
                    <div style={{
                      position: 'absolute',
                      bottom: 0,
                      left: 0,
                      right: 0,
                      height: '2px',
                      background: 'linear-gradient(90deg, rgba(24, 195, 186, 0.6), rgba(24, 195, 186, 0))',
                      opacity: isExpanded ? 1 : 0,
                      transition: 'opacity 0.3s ease'
                    }} />
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Call to Action - Link to Solutions */}
        <div className="text-center mt-5 pt-4">
          <p className="mb-3" style={{ fontSize: '1.1rem', color: '#D5D6CC' }}>
            Discover tailored solutions for your sector
          </p>
          <Link
            to="/solutions"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.75rem',
              padding: '1rem 2.5rem',
              background: 'linear-gradient(135deg, rgba(24, 195, 186, 0.15) 0%, rgba(24, 195, 186, 0.25) 100%)',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(24, 195, 186, 0.3)',
              borderRadius: '50px',
              color: '#18C3BA',
              textDecoration: 'none',
              fontSize: '1.1rem',
              fontWeight: 600,
              fontFamily: "'Montserrat', sans-serif",
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              boxShadow: '0 8px 25px rgba(24, 195, 186, 0.15)',
              position: 'relative',
              overflow: 'hidden'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'linear-gradient(135deg, rgba(24, 195, 186, 0.25) 0%, rgba(24, 195, 186, 0.35) 100%)';
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 12px 35px rgba(24, 195, 186, 0.25)';
              e.currentTarget.style.borderColor = 'rgba(24, 195, 186, 0.4)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'linear-gradient(135deg, rgba(24, 195, 186, 0.15) 0%, rgba(24, 195, 186, 0.25) 100%)';
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 8px 25px rgba(24, 195, 186, 0.15)';
              e.currentTarget.style.borderColor = 'rgba(24, 195, 186, 0.3)';
            }}
          >
            <span>Solutions</span>
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              style={{
                transition: 'transform 0.3s ease'
              }}
            >
              <path
                d="M7 17L17 7M17 7H7M17 7V17"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </Link>
        </div>
      </div>
    </section>
  );
};

export default UserPersonasSection;